import { MetricCollector, TelemetryMetric } from './types';

export class NetworkCollector implements MetricCollector {
  private apiCalls: Map<string, number[]> = new Map();
  private websocketConnections: Map<string, { connected: boolean; lastPing: number }> = new Map();
  private dataSizes: Map<string, number[]> = new Map();
  private userContext: { email?: string; full_name?: string; id?: string };

  constructor(userContext: { email?: string; full_name?: string; id?: string }) {
    this.userContext = userContext;
  }

  start(): void {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const startTime = performance.now();
      const url = typeof args[0] === 'string' ? args[0] : 'unknown';

      try {
        const response = await originalFetch(...args);
        const endTime = performance.now();
        const duration = endTime - startTime;

        this.recordApiCall(url, duration, response.headers.get('content-length'));
        return response;
      } catch (error) {
        const endTime = performance.now();
        this.recordApiCall(url, endTime - startTime, null, error);
        throw error;
      }
    };

    this.monitorWebSockets();
  }

  stop(): void {}

  collect(): TelemetryMetric[] {
    const metrics: TelemetryMetric[] = [];

    this.apiCalls.forEach((times, endpoint) => {
      const avgTime = times.reduce((sum, time) => sum + time, 0) / times.length;
      const p95Time = this.calculatePercentile(times, 95);

      metrics.push({
        name: 'api_avg_response_time',
        value: avgTime,
        unit: 'ms',
        timestamp: Date.now(),
        context: { endpoint, ...this.userContext },
        tags: ['network', 'api'],
      });

      metrics.push({
        name: 'api_p95_response_time',
        value: p95Time,
        unit: 'ms',
        timestamp: Date.now(),
        context: { endpoint, ...this.userContext },
        tags: ['network', 'api', 'percentile'],
      });
    });

    this.websocketConnections.forEach((status, wsUrl) => {
      metrics.push({
        name: 'websocket_connection_status',
        value: status.connected ? 1 : 0,
        unit: 'boolean',
        timestamp: Date.now(),
        context: { websocket: wsUrl, ...this.userContext },
        tags: ['network', 'websocket'],
      });
    });

    this.dataSizes.forEach((sizes, endpoint) => {
      const avgSize = sizes.reduce((sum, size) => sum + size, 0) / sizes.length;

      metrics.push({
        name: 'api_avg_response_size',
        value: avgSize,
        unit: 'bytes',
        timestamp: Date.now(),
        context: { endpoint, ...this.userContext },
        tags: ['network', 'data-size'],
      });
    });

    return metrics;
  }

  private recordApiCall(endpoint: string, duration: number, contentLength?: string | null, error?: any): void {
    if (!this.apiCalls.has(endpoint)) {
      this.apiCalls.set(endpoint, []);
    }
    this.apiCalls.get(endpoint)!.push(duration);

    if (contentLength) {
      const size = parseInt(contentLength, 10);
      if (!isNaN(size)) {
        if (!this.dataSizes.has(endpoint)) {
          this.dataSizes.set(endpoint, []);
        }
        this.dataSizes.get(endpoint)!.push(size);
      }
    }

    const times = this.apiCalls.get(endpoint)!;
    if (times.length > 50) {
      times.splice(0, times.length - 50);
    }
  }

  private calculatePercentile(values: number[], percentile: number): number {
    const sorted = [...values].sort((a, b) => a - b);
    const index = Math.ceil((percentile / 100) * sorted.length) - 1;
    return sorted[index] || 0;
  }

  private recordWebSocketEvent(url: string, eventType: string, value: number): void {
    if (!this.websocketConnections.has(url)) {
      this.websocketConnections.set(url, { connected: true, lastPing: Date.now() });
    }

    if (eventType === 'message_received') {
      this.websocketConnections.get(url)!.lastPing = Date.now();
    }

    if (eventType === 'open') {
      this.websocketConnections.get(url)!.connected = true;
    } else if (eventType === 'close' || eventType === 'error') {
      this.websocketConnections.get(url)!.connected = false;
    }
  }

  private monitorWebSockets(): void {
    const originalWebSocket = window.WebSocket;

    const collector = this;

    const WebSocketWrapper = function (this: WebSocket, url: string | URL, protocols?: string | string[]) {
      const ws = new originalWebSocket(url, protocols);
      const startTime = performance.now();

      ws.addEventListener('open', () => {
        const connectionTime = performance.now() - startTime;
        collector.recordWebSocketEvent(url.toString(), 'connection_time', connectionTime);
      });

      ws.addEventListener('message', event => {
        collector.recordWebSocketEvent(url.toString(), 'message_received', event.data.length);
      });

      ws.addEventListener('error', () => {
        collector.recordWebSocketEvent(url.toString(), 'error', 1);
      });

      ws.addEventListener('close', () => {
        collector.recordWebSocketEvent(url.toString(), 'disconnection', 1);
      });

      return ws;
    } as any;

    WebSocketWrapper.CONNECTING = originalWebSocket.CONNECTING;
    WebSocketWrapper.OPEN = originalWebSocket.OPEN;
    WebSocketWrapper.CLOSING = originalWebSocket.CLOSING;
    WebSocketWrapper.CLOSED = originalWebSocket.CLOSED;
    WebSocketWrapper.prototype = originalWebSocket.prototype;

    window.WebSocket = WebSocketWrapper;
  }
}
